import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Settings, 
  FileText, 
  Search, 
  Code, 
  Image as ImageIcon, 
  Link as LinkIcon, 
  Target,
  Sparkles,
  Brain,
  TrendingUp,
  Users,
  Palette,
  CheckCircle2,
  Info,
  Zap,
  Award,
  Globe,
  Type
} from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export interface AdvancedContentGenerationSettings {
  // Stage 1: Research & Strategic Planning
  use_competitor_analysis: boolean;
  competitive_gap_focus: string; // "differentiation" | "fill_gaps" | "improve_existing"
  target_search_intent: string; // "informational" | "commercial" | "transactional" | "mixed"
  target_audience_persona: string;
  content_depth: string; // "comprehensive" | "standard" | "focused"
  
  // Stage 2: Content Generation
  language: string;
  writing_style: string;
  custom_writing_style?: string;
  brand_voice: string; // "authoritative" | "friendly" | "professional" | "casual" | "technical"
  tone_modifiers: string[]; // ["empathetic", "confident", "educational", etc.]
  word_count_target: number;
  word_count_flexibility: number; // percentage +/-
  
  // E-E-A-T Integration
  demonstrate_expertise: boolean;
  demonstrate_experience: boolean;
  include_author_bio: boolean;
  include_citations: boolean;
  cite_authority_sources: boolean;
  
  // Content Structure
  article_format: string; // "ultimate_guide" | "how_to" | "listicle" | "comparison" | "case_study"
  heading_structure: string; // "traditional" | "question_based" | "benefit_driven"
  include_table_of_contents: boolean;
  include_faq_section: boolean;
  faq_count: number;
  include_key_takeaways: boolean;
  
  // LLMO (LLM Optimization)
  optimize_for_ai_search: boolean;
  use_clear_concise_language: boolean;
  use_question_answer_format: boolean;
  include_entity_rich_content: boolean;
  optimize_for_featured_snippets: boolean;
  
  // Stage 3: SEO & Technical Optimization
  primary_keyword_density: number; // percentage
  include_semantic_keywords: boolean;
  keyword_integration_style: string; // "natural" | "aggressive" | "minimal"
  
  // Schema Markup
  include_schema_markup: boolean;
  schema_types: string[]; // ["Article", "FAQPage", "HowTo", "Product", etc.]
  
  // Meta Optimization
  auto_generate_meta_title: boolean;
  auto_generate_meta_description: boolean;
  include_open_graph_tags: boolean;
  include_twitter_cards: boolean;
  
  // Internal Linking
  include_internal_links: boolean;
  internal_link_count: number;
  internal_link_strategy: string; // "contextual" | "sidebar" | "both"
  
  // Image Generation & Optimization
  include_images: boolean;
  image_count: number;
  image_style: string;
  custom_image_style?: string;
  image_format: string; // "realistic" | "illustration" | "infographic" | "mixed"
  image_optimization: boolean;
  generate_image_alt_text: boolean;
  
  // CTAs & Conversion Optimization
  include_cta: boolean;
  cta_placement: string; // "end" | "middle" | "both" | "multiple"
  cta_style: string; // "soft" | "direct" | "button" | "inline"
  
  // Technical SEO
  optimize_for_core_web_vitals: boolean;
  minify_html: boolean;
  lazy_load_images: boolean;
  
  // Branding
  brand_colors: string[];
  logo_url?: string;
  template_style: string;
  custom_template_css?: string;
}

interface AdvancedContentSettingsProps {
  onSave: (settings: AdvancedContentGenerationSettings) => void;
  onCancel: () => void;
  initialSettings?: Partial<AdvancedContentGenerationSettings>;
  analysisData?: any; // Pre-fill from analysis
  articleData?: any; // Context from the article being generated
}

const DEFAULT_SETTINGS: AdvancedContentGenerationSettings = {
  // Stage 1: Research & Strategic Planning
  use_competitor_analysis: true,
  competitive_gap_focus: "differentiation",
  target_search_intent: "mixed",
  target_audience_persona: "general",
  content_depth: "comprehensive",
  
  // Stage 2: Content Generation
  language: "en",
  writing_style: "professional",
  brand_voice: "professional",
  tone_modifiers: ["educational", "confident"],
  word_count_target: 2500,
  word_count_flexibility: 20,
  
  // E-E-A-T Integration
  demonstrate_expertise: true,
  demonstrate_experience: true,
  include_author_bio: false,
  include_citations: true,
  cite_authority_sources: true,
  
  // Content Structure
  article_format: "ultimate_guide",
  heading_structure: "traditional",
  include_table_of_contents: true,
  include_faq_section: true,
  faq_count: 5,
  include_key_takeaways: true,
  
  // LLMO (LLM Optimization)
  optimize_for_ai_search: true,
  use_clear_concise_language: true,
  use_question_answer_format: true,
  include_entity_rich_content: true,
  optimize_for_featured_snippets: true,
  
  // Stage 3: SEO & Technical Optimization
  primary_keyword_density: 1.5,
  include_semantic_keywords: true,
  keyword_integration_style: "natural",
  
  // Schema Markup
  include_schema_markup: true,
  schema_types: ["Article", "FAQPage"],
  
  // Meta Optimization
  auto_generate_meta_title: true,
  auto_generate_meta_description: true,
  include_open_graph_tags: true,
  include_twitter_cards: true,
  
  // Internal Linking
  include_internal_links: true,
  internal_link_count: 5,
  internal_link_strategy: "contextual",
  
  // Image Generation & Optimization
  include_images: true,
  image_count: 3,
  image_style: "professional",
  image_format: "realistic",
  image_optimization: true,
  generate_image_alt_text: true,
  
  // CTAs & Conversion Optimization
  include_cta: true,
  cta_placement: "both",
  cta_style: "button",
  
  // Technical SEO
  optimize_for_core_web_vitals: true,
  minify_html: false,
  lazy_load_images: true,
  
  // Branding
  brand_colors: ["#3B82F6", "#1E40AF"],
  template_style: "modern",
};

export const AdvancedContentSettings: React.FC<AdvancedContentSettingsProps> = ({
  onSave,
  onCancel,
  initialSettings,
  analysisData,
  articleData
}) => {
  const [settings, setSettings] = useState<AdvancedContentGenerationSettings>({
    ...DEFAULT_SETTINGS,
    ...initialSettings
  });

  const [activeTab, setActiveTab] = useState("strategy");
  const [hasChanges, setHasChanges] = useState(false);

  // Pre-fill from analysis data
  useEffect(() => {
    if (analysisData || articleData) {
      const prefilled = preFillFromAnalysis(analysisData, articleData);
      setSettings(prev => ({ ...prev, ...prefilled }));
    }
  }, [analysisData, articleData]);

  const preFillFromAnalysis = (analysis: any, article: any) => {
    const prefilled: Partial<AdvancedContentGenerationSettings> = {};

    if (article) {
      // Pre-fill from article data
      if (article.search_intent) {
        prefilled.target_search_intent = article.search_intent.toLowerCase();
      }
      if (article.target_audience_segment) {
        prefilled.target_audience_persona = article.target_audience_segment;
      }
      if (article.estimated_word_count) {
        prefilled.word_count_target = article.estimated_word_count;
      }
      if (article.ranking_difficulty) {
        // Adjust strategy based on difficulty
        const difficulty = article.ranking_difficulty.toLowerCase();
        if (difficulty === 'high') {
          prefilled.competitive_gap_focus = "differentiation";
          prefilled.content_depth = "comprehensive";
          prefilled.word_count_target = Math.max(article.estimated_word_count || 3000, 3000);
        } else if (difficulty === 'low') {
          prefilled.content_depth = "standard";
        }
      }
    }

    if (analysis) {
      // Pre-fill from analysis data
      if (analysis.primaryNiche) {
        prefilled.demonstrate_expertise = true;
        prefilled.cite_authority_sources = true;
      }
    }

    return prefilled;
  };

  const updateSetting = (key: keyof AdvancedContentGenerationSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onSave(settings);
    setHasChanges(false);
    toast.success("Settings saved successfully!");
  };

  const resetToDefaults = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasChanges(true);
    toast.info("Reset to default settings");
  };

  const InfoTooltip = ({ content }: { content: string }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-4 w-4 text-muted-foreground cursor-help inline ml-1" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-sm">{content}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Advanced Content Generation
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure comprehensive settings for high-quality, SEO-optimized content
          </p>
        </div>
        <Button onClick={resetToDefaults} variant="outline" size="sm">
          Reset to Defaults
        </Button>
      </div>

      {/* Pre-filled Notice */}
      {(analysisData || articleData) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Settings Pre-filled from Analysis</h4>
            <p className="text-sm text-blue-700 mt-1">
              Based on competitive analysis and article metrics, these settings are optimized for best performance. You can customize any setting below.
            </p>
          </div>
        </div>
      )}

      {/* Main Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="strategy" className="gap-2">
            <Target className="h-4 w-4" />
            Strategy
          </TabsTrigger>
          <TabsTrigger value="content" className="gap-2">
            <FileText className="h-4 w-4" />
            Content
          </TabsTrigger>
          <TabsTrigger value="seo" className="gap-2">
            <Search className="h-4 w-4" />
            SEO
          </TabsTrigger>
          <TabsTrigger value="technical" className="gap-2">
            <Code className="h-4 w-4" />
            Technical
          </TabsTrigger>
          <TabsTrigger value="branding" className="gap-2">
            <Palette className="h-4 w-4" />
            Branding
          </TabsTrigger>
        </TabsList>

        {/* Strategy Tab */}
        <TabsContent value="strategy" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Research & Strategic Planning
              </CardTitle>
              <CardDescription>
                Define your content strategy and competitive positioning
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Competitor Analysis */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-base">Use Competitor Analysis</Label>
                    <p className="text-sm text-muted-foreground">
                      Leverage competitive intelligence from FireCrawl data
                    </p>
                  </div>
                  <Switch
                    checked={settings.use_competitor_analysis}
                    onCheckedChange={(checked) => updateSetting('use_competitor_analysis', checked)}
                  />
                </div>

                {settings.use_competitor_analysis && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                    <Label>Competitive Gap Focus</Label>
                    <Select
                      value={settings.competitive_gap_focus}
                      onValueChange={(value) => updateSetting('competitive_gap_focus', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="differentiation">Differentiation - Create unique content</SelectItem>
                        <SelectItem value="fill_gaps">Fill Gaps - Cover missing topics</SelectItem>
                        <SelectItem value="improve_existing">Improve Existing - Beat competitors</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Separator />

              {/* Search Intent */}
              <div className="space-y-2">
                <Label>
                  Target Search Intent
                  <InfoTooltip content="The primary purpose behind the user's search query. This affects content structure and CTAs." />
                </Label>
                <Select
                  value={settings.target_search_intent}
                  onValueChange={(value) => updateSetting('target_search_intent', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="informational">Informational - Education focused</SelectItem>
                    <SelectItem value="commercial">Commercial - Product/service research</SelectItem>
                    <SelectItem value="transactional">Transactional - Ready to buy</SelectItem>
                    <SelectItem value="mixed">Mixed - Multiple intents</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Audience Persona */}
              <div className="space-y-2">
                <Label>Target Audience Persona</Label>
                <Textarea
                  value={settings.target_audience_persona}
                  onChange={(e) => updateSetting('target_audience_persona', e.target.value)}
                  placeholder="e.g., Mid-level marketing managers at B2B SaaS companies, 25-40 years old"
                  rows={3}
                />
              </div>

              {/* Content Depth */}
              <div className="space-y-2">
                <Label>
                  Content Depth
                  <InfoTooltip content="How comprehensive should the article be? Comprehensive works best for high-difficulty keywords." />
                </Label>
                <Select
                  value={settings.content_depth}
                  onValueChange={(value) => updateSetting('content_depth', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="focused">Focused - Quick, specific answer</SelectItem>
                    <SelectItem value="standard">Standard - Good depth, balanced</SelectItem>
                    <SelectItem value="comprehensive">Comprehensive - Ultimate guide style</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Content Tab */}
        <TabsContent value="content" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" />
                Content Generation Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Language & Style */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select
                    value={settings.language}
                    onValueChange={(value) => updateSetting('language', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="lt">Lithuanian</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="pt">Portuguese</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Writing Style</Label>
                  <Select
                    value={settings.writing_style}
                    onValueChange={(value) => updateSetting('writing_style', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="authoritative">Authoritative</SelectItem>
                      <SelectItem value="conversational">Conversational</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Word Count */}
              <div className="space-y-2">
                <Label>Target Word Count: {settings.word_count_target.toLocaleString()} words</Label>
                <Input
                  type="range"
                  min="500"
                  max="5000"
                  step="100"
                  value={settings.word_count_target}
                  onChange={(e) => updateSetting('word_count_target', parseInt(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>500</span>
                  <span>2500</span>
                  <span>5000</span>
                </div>
              </div>

              <Separator />

              {/* E-E-A-T Integration */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold">E-E-A-T Integration</h4>
                  <InfoTooltip content="Experience, Expertise, Authoritativeness, Trustworthiness - Critical for Google rankings" />
                </div>

                <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                  <div className="flex items-center justify-between">
                    <Label>Demonstrate Expertise</Label>
                    <Switch
                      checked={settings.demonstrate_expertise}
                      onCheckedChange={(checked) => updateSetting('demonstrate_expertise', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Demonstrate Experience</Label>
                    <Switch
                      checked={settings.demonstrate_experience}
                      onCheckedChange={(checked) => updateSetting('demonstrate_experience', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Include Citations</Label>
                    <Switch
                      checked={settings.include_citations}
                      onCheckedChange={(checked) => updateSetting('include_citations', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Cite Authority Sources</Label>
                    <Switch
                      checked={settings.cite_authority_sources}
                      onCheckedChange={(checked) => updateSetting('cite_authority_sources', checked)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Content Structure */}
              <div className="space-y-4">
                <h4 className="font-semibold">Content Structure</h4>

                <div className="space-y-2">
                  <Label>Article Format</Label>
                  <Select
                    value={settings.article_format}
                    onValueChange={(value) => updateSetting('article_format', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ultimate_guide">Ultimate Guide</SelectItem>
                      <SelectItem value="how_to">How-To Guide</SelectItem>
                      <SelectItem value="listicle">Listicle</SelectItem>
                      <SelectItem value="comparison">Comparison</SelectItem>
                      <SelectItem value="case_study">Case Study</SelectItem>
                      <SelectItem value="thought_leadership">Thought Leadership</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Table of Contents</Label>
                    <Switch
                      checked={settings.include_table_of_contents}
                      onCheckedChange={(checked) => updateSetting('include_table_of_contents', checked)}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Key Takeaways</Label>
                    <Switch
                      checked={settings.include_key_takeaways}
                      onCheckedChange={(checked) => updateSetting('include_key_takeaways', checked)}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm">FAQ Section</Label>
                    <p className="text-xs text-muted-foreground">Boost featured snippet chances</p>
                  </div>
                  <Switch
                    checked={settings.include_faq_section}
                    onCheckedChange={(checked) => updateSetting('include_faq_section', checked)}
                  />
                </div>

                {settings.include_faq_section && (
                  <div className="space-y-2 pl-4">
                    <Label className="text-sm">Number of FAQs: {settings.faq_count}</Label>
                    <Input
                      type="range"
                      min="3"
                      max="10"
                      value={settings.faq_count}
                      onChange={(e) => updateSetting('faq_count', parseInt(e.target.value))}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* LLMO Card */}
          <Card className="border-purple-200 bg-purple-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-600" />
                LLM Optimization (LLMO)
              </CardTitle>
              <CardDescription>
                Optimize for AI-powered search engines (ChatGPT, Perplexity, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Optimize for AI Search</Label>
                <Switch
                  checked={settings.optimize_for_ai_search}
                  onCheckedChange={(checked) => updateSetting('optimize_for_ai_search', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Use Clear, Concise Language</Label>
                <Switch
                  checked={settings.use_clear_concise_language}
                  onCheckedChange={(checked) => updateSetting('use_clear_concise_language', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Question-Answer Format</Label>
                <Switch
                  checked={settings.use_question_answer_format}
                  onCheckedChange={(checked) => updateSetting('use_question_answer_format', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Entity-Rich Content</Label>
                <Switch
                  checked={settings.include_entity_rich_content}
                  onCheckedChange={(checked) => updateSetting('include_entity_rich_content', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                SEO Optimization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Keyword Optimization */}
              <div className="space-y-4">
                <h4 className="font-semibold">Keyword Integration</h4>

                <div className="space-y-2">
                  <Label>Primary Keyword Density: {settings.primary_keyword_density}%</Label>
                  <Input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    value={settings.primary_keyword_density}
                    onChange={(e) => updateSetting('primary_keyword_density', parseFloat(e.target.value))}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0.5% (Minimal)</span>
                    <span>1.5% (Optimal)</span>
                    <span>3% (Aggressive)</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Keyword Integration Style</Label>
                  <Select
                    value={settings.keyword_integration_style}
                    onValueChange={(value) => updateSetting('keyword_integration_style', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimal - Very natural</SelectItem>
                      <SelectItem value="natural">Natural - Recommended</SelectItem>
                      <SelectItem value="aggressive">Aggressive - More keywords</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Include Semantic Keywords</Label>
                  <Switch
                    checked={settings.include_semantic_keywords}
                    onCheckedChange={(checked) => updateSetting('include_semantic_keywords', checked)}
                  />
                </div>
              </div>

              <Separator />

              {/* Schema Markup */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h4 className="font-semibold">Schema Markup</h4>
                    <p className="text-sm text-muted-foreground">Structured data for rich results</p>
                  </div>
                  <Switch
                    checked={settings.include_schema_markup}
                    onCheckedChange={(checked) => updateSetting('include_schema_markup', checked)}
                  />
                </div>

                {settings.include_schema_markup && (
                  <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                    <Label className="text-sm">Schema Types</Label>
                    <div className="flex flex-wrap gap-2">
                      {["Article", "FAQPage", "HowTo", "Review", "Product"].map((type) => (
                        <Badge
                          key={type}
                          variant={settings.schema_types.includes(type) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => {
                            const newTypes = settings.schema_types.includes(type)
                              ? settings.schema_types.filter(t => t !== type)
                              : [...settings.schema_types, type];
                            updateSetting('schema_types', newTypes);
                          }}
                        >
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Meta Tags */}
              <div className="space-y-3">
                <h4 className="font-semibold">Meta Optimization</h4>
                <div className="flex items-center justify-between">
                  <Label>Auto-generate Meta Title</Label>
                  <Switch
                    checked={settings.auto_generate_meta_title}
                    onCheckedChange={(checked) => updateSetting('auto_generate_meta_title', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Auto-generate Meta Description</Label>
                  <Switch
                    checked={settings.auto_generate_meta_description}
                    onCheckedChange={(checked) => updateSetting('auto_generate_meta_description', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Open Graph Tags</Label>
                  <Switch
                    checked={settings.include_open_graph_tags}
                    onCheckedChange={(checked) => updateSetting('include_open_graph_tags', checked)}
                  />
                </div>
              </div>

              <Separator />

              {/* Internal Linking */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h4 className="font-semibold flex items-center gap-2">
                      <LinkIcon className="h-4 w-4" />
                      Internal Linking
                    </h4>
                    <p className="text-sm text-muted-foreground">Build topic cluster authority</p>
                  </div>
                  <Switch
                    checked={settings.include_internal_links}
                    onCheckedChange={(checked) => updateSetting('include_internal_links', checked)}
                  />
                </div>

                {settings.include_internal_links && (
                  <div className="space-y-2 pl-4">
                    <Label className="text-sm">Number of Internal Links: {settings.internal_link_count}</Label>
                    <Input
                      type="range"
                      min="2"
                      max="10"
                      value={settings.internal_link_count}
                      onChange={(e) => updateSetting('internal_link_count', parseInt(e.target.value))}
                    />
                  </div>
                )}
              </div>

              <Separator />

              {/* Images */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h4 className="font-semibold flex items-center gap-2">
                      <ImageIcon className="h-4 w-4" />
                      Image Generation
                    </h4>
                    <p className="text-sm text-muted-foreground">AI-generated optimized images</p>
                  </div>
                  <Switch
                    checked={settings.include_images}
                    onCheckedChange={(checked) => updateSetting('include_images', checked)}
                  />
                </div>

                {settings.include_images && (
                  <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                    <div className="space-y-2">
                      <Label className="text-sm">Number of Images: {settings.image_count}</Label>
                      <Input
                        type="range"
                        min="0"
                        max="8"
                        value={settings.image_count}
                        onChange={(e) => updateSetting('image_count', parseInt(e.target.value))}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm">Image Style</Label>
                      <Select
                        value={settings.image_style}
                        onValueChange={(value) => updateSetting('image_style', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="realistic">Realistic</SelectItem>
                          <SelectItem value="minimalist">Minimalist</SelectItem>
                          <SelectItem value="modern">Modern</SelectItem>
                          <SelectItem value="illustration">Illustration</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Generate Alt Text</Label>
                      <Switch
                        checked={settings.generate_image_alt_text}
                        onCheckedChange={(checked) => updateSetting('generate_image_alt_text', checked)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Technical Tab */}
        <TabsContent value="technical" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Technical SEO
              </CardTitle>
              <CardDescription>
                Core Web Vitals and performance optimization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Optimize for Core Web Vitals</Label>
                  <p className="text-xs text-muted-foreground">LCP, FID, CLS optimization</p>
                </div>
                <Switch
                  checked={settings.optimize_for_core_web_vitals}
                  onCheckedChange={(checked) => updateSetting('optimize_for_core_web_vitals', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Lazy Load Images</Label>
                <Switch
                  checked={settings.lazy_load_images}
                  onCheckedChange={(checked) => updateSetting('lazy_load_images', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Minify HTML Output</Label>
                <Switch
                  checked={settings.minify_html}
                  onCheckedChange={(checked) => updateSetting('minify_html', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* CTA Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Conversion Optimization
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Include Call-to-Actions</Label>
                <Switch
                  checked={settings.include_cta}
                  onCheckedChange={(checked) => updateSetting('include_cta', checked)}
                />
              </div>

              {settings.include_cta && (
                <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                  <div className="space-y-2">
                    <Label className="text-sm">CTA Placement</Label>
                    <Select
                      value={settings.cta_placement}
                      onValueChange={(value) => updateSetting('cta_placement', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="end">End Only</SelectItem>
                        <SelectItem value="middle">Middle Only</SelectItem>
                        <SelectItem value="both">Start & End</SelectItem>
                        <SelectItem value="multiple">Multiple Throughout</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm">CTA Style</Label>
                    <Select
                      value={settings.cta_style}
                      onValueChange={(value) => updateSetting('cta_style', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="soft">Soft - Subtle suggestion</SelectItem>
                        <SelectItem value="direct">Direct - Clear ask</SelectItem>
                        <SelectItem value="button">Button - Prominent CTA</SelectItem>
                        <SelectItem value="inline">Inline - Within content</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Brand & Template
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Template Style</Label>
                <Select
                  value={settings.template_style}
                  onValueChange={(value) => updateSetting('template_style', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="classic">Classic</SelectItem>
                    <SelectItem value="minimal">Minimal</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                    <SelectItem value="creative">Creative</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Brand Colors (Hex codes, comma-separated)</Label>
                <Input
                  value={settings.brand_colors.join(", ")}
                  onChange={(e) => {
                    const colors = e.target.value.split(",").map(c => c.trim());
                    updateSetting('brand_colors', colors);
                  }}
                  placeholder="#3B82F6, #1E40AF"
                />
                <div className="flex gap-2 mt-2">
                  {settings.brand_colors.map((color, idx) => (
                    <div
                      key={idx}
                      className="w-10 h-10 rounded border"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-6 border-t">
        <Button onClick={onCancel} variant="outline">
          Cancel
        </Button>
        <Button onClick={handleSave} size="lg" className="gap-2" disabled={!hasChanges}>
          <CheckCircle2 className="h-4 w-4" />
          Save & Generate
        </Button>
      </div>
    </div>
  );
};

